import { Box, IconButton, Tooltip, Typography, styled } from '@mui/material'
import { KeyboardDoubleArrowLeft as ChevronDoubleLeftIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'

import { BrandMark } from '@/apps/latest/components/atoms/BrandMark'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useReleaseChannel, type ReleaseChannel } from '@/features/auth'
import { useBootReveal } from '@/shared/hooks'
import { DURATION, EASING, fadeInOnce } from '@/shared/styles/motion'
import { RADIUS } from '@/shared/styles/radii'
import { LAYOUT, SPACING_PX } from '@/shared/styles/spacing'
import {
  scaledFontSize,
  FONT_WEIGHT,
  ICON_SIZE,
} from '@/shared/styles/typography'

const SIDEBAR_WIDTH = LAYOUT.sidebarWidth

// 幅は文字サイズモードに追従する (ADR-0019: 224 × fontScale, 丸め).
// 文字だけ拡大して幅が固定だと患者名の途中切れが増え、 拡大の目的と矛盾するため.
const Container = styled(Box)(({ theme }) => ({
  width: Math.round(SIDEBAR_WIDTH * (theme.fontScale ?? 1)),
  flexShrink: 0,
  background: colorTokens.bg.subtle,
  borderRight: `1px solid ${colorTokens.border.default}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'sticky',
  top: 0,
}))

// hover の当たりを WorkspaceRow 全体にするため、 トグル側は class で受ける.
const TOGGLE_CLASS = 'workspace-collapse-toggle'

// 文字は文字サイズモードに追従する (ADR-0019: サイドバーに載る文字は全て追従).
// styled へ静的オブジェクトを渡すと scaledFontSize の callback が評価されないため、
// theme を受ける callback 形で書く (typography.ts の禁則).
const WorkspaceRow = styled(Box)(({ theme }) => ({
  height: LAYOUT.sidebarWorkspaceRowHeight,
  padding: `0 ${SPACING_PX[3]}px`,
  display: 'flex',
  alignItems: 'center',
  fontSize: scaledFontSize('label')(theme),
  fontWeight: FONT_WEIGHT.semibold,
  color: colorTokens.fg.default,
  // 折りたたみトグルは hover 表示 (hover-only-chrome: 開閉は低頻度の補助操作).
  // レイアウトシフトを起こさないよう、 場所は常に確保して opacity だけ変える.
  [`&:hover .${TOGGLE_CLASS}, &:focus-within .${TOGGLE_CLASS}`]: {
    opacity: 1,
  },
}))

// 表示するのはリリース段階 (全店 / 先行版 / 共同開発版) であって、 機能フラグ名ではない。
// 全店 (ga) は「特別な版ではない」ので何も出さない = 大多数の画面は現状と変わらない。
//
// 派生 boolean (isBeta / isAlpha) ではなく channel から直に引く。 同型 2 引数だと
// 呼び違えても型が通り、 β 店舗に「共同開発版」と出しても誰も気付けない。 Record なら
// channel が増えたときに **型エラーで気付ける** (取りこぼして無言で無表示にならない)。
export const STAGE_LABEL: Record<ReleaseChannel, string | null> = {
  ga: null,
  beta: '先行版',
  alpha: '共同開発版',
}

interface SidebarShellProps {
  onToggle?: () => void
  // プロダクト名の右に出す版の名前。渡さなければリリース段階 (先行版 / 共同開発版) を出す。
  // 渡したときは段階の**代わり**に出る (行に並ぶ語は常に 1 つ)。語彙は呼び出し側が持つ。
  versionLabel?: string
  // 見出し行の下に積む中身 (薬剤師: IconBar + ContextPane / 管理栄養士: お客様を探す + その日の記録)。
  // 高さの配り方は中身の責務なので、 ここは flex 列に徹する。
  children: ReactNode
}

// 左ペインの枠: 幅・背景・見出し行 (マーク + Kakeru + 版のラベル + 折りたたみトグル)。
// 系統 (薬剤師 / 管理栄養士) を知らず、 見出し行の下に何を積むかは呼び出し側が決める。
export const SidebarShell = ({
  onToggle,
  versionLabel,
  children,
}: SidebarShellProps) => {
  const navigate = useNavigate()
  const { channel } = useReleaseChannel()
  const label = versionLabel ?? STAGE_LABEL[channel]
  // 起動モーション (ADR-0026) はマークの一拍だけ。 行・ペイン全体は動かさない。
  // 折りたたみ → 再展開は unmount → mount なので、 初回表示かの判定を挟む。
  const isBoot = useBootReveal()

  return (
    <Container>
      <WorkspaceRow>
        {/*
          クリックでホームへ戻れるのはマーク + 名前まで (改修前は行全体だった)。
          行全体にすると段階ラベルを押しても遷移し、 hover の面がラベルまで伸びて
          「ラベルも押せるもの」に見える。
          '/' はどちらの系統でもその系統のホームへ振り分けられる (pages/profession-dispatchers)。
        */}
        <Box
          role="button"
          tabIndex={0}
          onClick={() => navigate('/')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              navigate('/')
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            borderRadius: `${RADIUS.sm}px`,
            px: 0.5,
            mx: -0.5,
            py: 0.25,
            '&:hover': { background: colorTokens.overlay.selected },
            // 状態が別視覚 (面) で伝わる neutral chrome なので ring はグレー
            // (design-book 01-foundations/accessibility.md).
            '&:focus-visible': {
              outline: 'none',
              boxShadow: colorTokens.focus.ringNeutral,
            },
          }}
        >
          <BrandMark boot={isBoot} />
          <Typography
            component="span"
            sx={{
              fontSize: scaledFontSize('label'),
              fontWeight: FONT_WEIGHT.semibold,
              // マークに半歩遅れて灯る (左面 0ms → 右面 120ms → 名前 → ラベル).
              ...(isBoot ? fadeInOnce(DURATION.fast, DURATION.fast) : {}),
            }}
          >
            Kakeru
          </Typography>
        </Box>
        {/*
          版のラベルはクリック領域の外に置く。 中に入れるとラベルを押したつもりで
          ホームへ飛ぶし、 hover の面がラベルまで伸びて「押せるもの」に見えてしまう。
        */}
        {label ? (
          <Typography
            component="span"
            sx={{
              ml: 1,
              fontSize: scaledFontSize('tag'),
              fontWeight: FONT_WEIGHT.medium,
              // fg.faint は三次情報 (placeholder・時刻) 用で bg.subtle 上 3.1:1 (ADR-0020).
              // 段を取り違えて読まれると困る語なので、 三次に落とさない.
              color: colorTokens.fg.muted,
              whiteSpace: 'nowrap',
              ...(isBoot ? fadeInOnce(DURATION.fast, DURATION.base) : {}),
            }}
          >
            {label}
          </Typography>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {onToggle ? (
          <Tooltip title="サイドバーを閉じる">
            <IconButton
              size="small"
              onClick={onToggle}
              className={TOGGLE_CLASS}
              sx={{
                p: 0.5,
                opacity: 0,
                transition: `opacity ${DURATION.fast}ms ${EASING.default}`,
                // タッチには hover が無いので常時表示に倒す (hover-only-chrome).
                '@media (hover: none)': { opacity: 1 },
              }}
            >
              <ChevronDoubleLeftIcon sx={{ fontSize: ICON_SIZE.sm }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </WorkspaceRow>
      {children}
    </Container>
  )
}

export const SIDEBAR_WIDTH_VALUE = SIDEBAR_WIDTH
